/* eslint-disable camelcase */
import { InstanceConfigDataModelSettings, IntegrationInstance } from 'models'

/**
 * Templates name vendor record fields; op-view rows and asset members carry model columns instead.
 * Tried in order when the record has no field of the template's name.
 */
const MODEL_COLUMN_FALLBACKS: Record<string, string[]> = {
  device_id: ['ASSET_ID', 'asset_id'],
  id: ['USER_ID', 'ASSET_ID'],
  uuid: ['USER_ID', 'ASSET_ID'],
  email: ['EMAILS_ADDRESSES', 'EMAIL_ADDRESS']
}

/* A model column can hold an array (or its JSON text) where the template wants one value. */
const firstElement = (value: unknown): unknown => {
  if (Array.isArray(value)) return value[0] ?? null

  if (typeof value === 'string' && value.startsWith('[')) {
    try {
      const parsed = JSON.parse(value)

      return Array.isArray(parsed) ? parsed[0] ?? null : value
    } catch {
      return value
    }
  }

  return value
}

const fallbackValue = (rowData: Record<string, unknown>, sourceName: string): unknown => {
  for (const column of MODEL_COLUMN_FALLBACKS[sourceName] ?? []) {
    const value = firstElement(rowData[column])

    if (value) return value
  }

  return null
}

export const generateRowUrl = <T extends object>(
  rowData: T,
  dataSourceOptions: IntegrationInstance[]
): string | null => {
  const instanceName = (rowData as Record<string, unknown>).ONYXIA_PRODUCT_INSTANCE

  if (!instanceName) return null

  const matchingDataSource = dataSourceOptions.find((option) => option.instance_name === instanceName)

  if (!matchingDataSource?.config?.data_model_settings?.[0]) return null

  const dataModelSetting: InstanceConfigDataModelSettings = matchingDataSource.config.data_model_settings[0]
  const { uri_template, uri_attributes } = dataModelSetting

  if (!uri_template || !uri_attributes?.length) return null

  let finalUrl = uri_template

  for (const attribute of uri_attributes) {
    // Check for array index pattern like {emails[0]}
    const arrayIndexMatch = finalUrl.match(new RegExp(`\\{${attribute.name}\\[(\\d+)\\]\\}`))
    const placeholder = arrayIndexMatch ? `{${attribute.name}[${arrayIndexMatch[1]}]}` : `{${attribute.name}}`
    const arrayIndex = arrayIndexMatch ? parseInt(arrayIndexMatch[1], 10) : null

    if (!finalUrl.includes(placeholder)) continue

    let replacementValue: string | null = null

    if (attribute.source_type === 'record') {
      if (attribute.source_name) {
        const sourceNameLower = attribute.source_name.toLowerCase()
        const sourceNameUpper = attribute.source_name.toUpperCase()

        let rawValue =
          (rowData as any)[attribute.source_name] ||
          (rowData as any)[sourceNameUpper] ||
          (rowData as any)[sourceNameLower] ||
          null

        if (!rawValue && arrayIndex === null) {
          rawValue = fallbackValue(rowData as Record<string, unknown>, sourceNameLower)
        }

        // Handle array index extraction if needed
        if (rawValue && arrayIndex !== null) {
          try {
            // Parse the value if it's a JSON array string
            const parsedArray = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue
            if (Array.isArray(parsedArray) && parsedArray.length > arrayIndex) {
              replacementValue = parsedArray[arrayIndex]
            }
          } catch (e) {
            // If parsing fails, try to extract from string format like "[\"a\",\"b\"]"
            console.error('Error parsing array value:', e)
            replacementValue = null
          }
        } else {
          replacementValue = rawValue
        }
      }
    } else if (attribute.source_type === 'config') {
      replacementValue = attribute.value || null
    }

    if (!replacementValue) return null

    finalUrl = finalUrl.replace(placeholder, replacementValue)
  }

  return finalUrl
}
